import { MutableRefObject, useMemo } from "react";
import { useCategoryTreeContext } from "src/hooks/context/use-category-tree-context";
import { CategoryNode } from "./CategoryNode";
import { CategoryTreeSkeleton } from "./CategoryTreeSkeleton";
import { Tree } from "src/atoms/Tree";
import { useCategoryTree } from "src/hooks/algolia/useCategoryTree";
import dynamic from "next/dynamic";
import { PLPTypes } from "src/types/PLPType.d";
import { TreeScrollWrapper } from "src/atoms/Tree/Tree.styled";
import { ShopType } from "src/types/graphql.d";
import { localeFix } from "src/utils/genderLocale/genderLocale";

const renderParentCategoryPageTypes: PLPTypes[] = ["BRAND_PLP"];

function CategoryTreeComponent({
  gender,
  brandName,
  categoryPageId,
  pageContentType,
  gridRef,
}: {
  gender: string;
  categoryPageId?: string;
  brandName?: string;
  pageContentType: PLPTypes;
  gridRef: MutableRefObject<HTMLDivElement | null>;
}): JSX.Element {
  useCategoryTree({ gender, categoryPageId, pageContentType, brandName });

  const { tree, parentIds, rootIds, isLoading } = useCategoryTreeContext();

  const renderParentCategory = useMemo(
    () =>
      renderParentCategoryPageTypes.includes(pageContentType) ||
      localeFix({ gender }) === ShopType.Kids,
    [pageContentType, gender]
  );

  const rootItemIds = useMemo(
    () => (renderParentCategory ? rootIds : parentIds),
    [rootIds, parentIds, renderParentCategory]
  );

  const filteredTree = useMemo(() => {
    return tree.filter((item) => rootItemIds.includes(item.itemId));
  }, [tree, rootItemIds]);

  if (!isLoading) {
    return (
      <Tree>
        <TreeScrollWrapper>
          {filteredTree.map((item) => {
            return (
              <CategoryNode
                key={item.itemId}
                category={item}
                rootItemIds={rootItemIds}
                gridRef={gridRef}
              />
            );
          })}
        </TreeScrollWrapper>
      </Tree>
    );
  }

  return <CategoryTreeSkeleton />;
}

const CategoryTree = dynamic(() => Promise.resolve(CategoryTreeComponent), {
  ssr: false,
  loading: () => <CategoryTreeSkeleton />,
});

export { CategoryTree };
